/* eslint-disable @typescript-eslint/no-explicit-any */
import slug from '@teko/slug';
import { NextRouter } from 'next/router';
import { buildSlugUrl } from '.';
import { DiscoveryBlock, DiscoveryItem, PageTypeEnum } from '@teko/discovery';
import { LinkInterface } from '@teko/types/ds';
import { parseJsonStringSafely } from '@teko/utils';
export {
  getObjFromQueryString,
  isValidEmailFormat,
  isValidPhoneFormat,
} from '@teko/utils';
import { MenuItem } from '@teko/discovery';
import { formatCurrency as libFormatCurrency } from '@teko/ds-desktop';

declare global {
  interface Window {
    MSStream: any;
  }
}

export const formatCurrency = (
  money: number | string,
  isFull = true,
): string => {
  return libFormatCurrency(money, { hideCurrencySymbol: !isFull });
};

export const getDiscountPercent = (
  finalPrice: number,
  oldPrice: number,
): number => {
  let discount = 0;
  if (finalPrice && oldPrice) {
    discount = 100 - Math.round((finalPrice * 100) / oldPrice);
  }
  return discount;
};

export const saveRecentlyProduct = (sku: string, localKey: string): void => {
  const historyStorage = localStorage.getItem(localKey);
  if (!historyStorage) {
    localStorage.setItem(localKey, JSON.stringify([sku]));
    return;
  }
  let histories = parseJsonStringSafely(historyStorage, []);
  histories = histories.filter((item: string) => item !== sku);
  histories = [sku, ...histories];
  if (histories.length > 20) {
    histories.pop();
  }
  localStorage.setItem(localKey, JSON.stringify(histories));
};

export const isIOSDevice = () => {
  const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  return iOS;
};
export const isAndroidDevice = () => {
  const android = /(android)/i.test(navigator.userAgent);
  return android;
};
// Check path visible navigation bottom bar
export const checkPath = (router: NextRouter, path: string): boolean => {
  let pathname = router.asPath || router.pathname;
  pathname === '' ? '/' : pathname;
  const searchParam = pathname.indexOf('?');
  pathname = searchParam >= 0 ? pathname.substring(0, searchParam) : pathname;
  const rawPath =
    path.indexOf('?') >= 0 ? path.substring(0, path.indexOf('?')) : path;
  return pathname === rawPath;
};

export const checkExpiryDateTimestamp = (
  startDate: string,
  endDate: string,
): boolean => {
  const today = new Date().getTime();
  if (
    today - new Date(parseInt(startDate) * 1000).getTime() > 0 &&
    new Date(parseInt(endDate) * 1000).getTime() - today > 0
  )
    return true;
  return false;
};
const convertUrlParamsToObject = (urlParams: URLSearchParams) => {
  const urlParamsArray = Array.from(urlParams);
  return urlParamsArray.reduce(
    (acc, cur) => ({
      ...acc,
      [cur[0]]: cur[1],
    }),
    {},
  );
};
export const computeBlockPageUrl = (menu: MenuItem): LinkInterface => {
  if (!menu.targetPath || menu.urlType !== 'rewrite') return null;
  const menuConfig = menu.configs;
  const targetFilterParams = convertUrlParamsToObject(
    new URLSearchParams(menu.targetPath),
  );
  // targetPath has only 1 filter config
  if (Object.keys(targetFilterParams)?.length === 1) {
    const filterKey = Object.keys(targetFilterParams)[0];
    const filterValue =
      menuConfig[filterKey]?.length === 1 ? menuConfig[filterKey][0] : null;
    if (!filterValue) return null;
    switch (filterKey) {
      case 'brands':
        return {
          href: {
            pathname: '/brands/[brandId]',
            query: {
              brandId: filterValue,
            },
          },
          target: menu.isTargetBlank ? '_blank' : '_self',
          as: {
            pathname: buildSlugUrl(PageTypeEnum.BRAND, menu.name, filterValue),
          },
          shallow: true,
        };
      case 'masterCategoryIds':
        return {
          href: {
            pathname: '/block-categories/[categoryId]',
            query: {
              categoryId: filterValue,
            },
          },
          target: menu.isTargetBlank ? '_blank' : '_self',
          as: buildSlugUrl(PageTypeEnum.CATEGORY, menu.name, filterValue),
          shallow: true,
        };
      case 'sellerIds':
        return {
          href: {
            pathname: '/sellers/[sellerId]',
            query: {
              sellerId: filterValue,
            },
          },
          target: menu.isTargetBlank ? '_blank' : '_self',
          as: buildSlugUrl(PageTypeEnum.SELLER, menu.name, filterValue),
          shallow: true,
        };
      case 'categories':
        return {
          href: {
            pathname: '/seller-categories/[sellerCategoryId]',
            query: {
              sellerCategoryId: filterValue,
            },
          },
          target: menu.isTargetBlank ? '_blank' : '_self',
          as: `/${slug(menu.name)}-scat.${filterValue}`,
          shallow: true,
        };
      default:
        return null;
    }
  }
  return null;
};

export const isHomepage = (pathname: string): boolean => {
  // Old version
  // return ['', '/'].includes(pathname);
  // This's added for supporting multi-languages
  return !pathname || /^\/(\?lang=[a-zA-Z-]{2,5})?$/.test(pathname);
};

export const filterBlockItems = (
  item: DiscoveryItem,
  blockList: DiscoveryBlock[],
): boolean => {
  const matchFormat = item.url.match(/^#\d+$/);
  if (!matchFormat) return false;
  const blockId = item.url.substring(1);
  const block = blockList.find((e) => e.id === blockId);
  if (!block) return false;
  return true;
};

export const parsePathName = (pathNameWithSearchParams: string) => {
  const path = pathNameWithSearchParams.match(/([^?]*)\?*([^?]*)/);
  const pathName = path?.[1];
  const searchParams = new URLSearchParams(path?.[2]);
  return {
    pathName,
    searchParams,
  };
};

export const getBaseDomain = () => {
  if (typeof window === 'undefined') return '';
  return `${window?.location?.protocol}/${window?.location?.host}`;
};
